import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { KeywordWithIntent } from '../types';
import { isCopiedKeyword } from '../utils/keywordCopy';

interface KeywordPillProps {
  keyword: KeywordWithIntent;
  productId: string;
  isSelected: boolean;
  isCustom: boolean;
  showVolume: boolean;
  showIntentScore: boolean;
  intentColor?: string;
  intentLabel?: string;
  onToggle: (productId: string, keywordText: string) => void;
  onDelete?: (productId: string, keywordText: string) => void;
  onRemoveCopy?: (productId: string, keywordText: string) => void;
  onDragStart?: (e: React.DragEvent, keyword: KeywordWithIntent, productId: string) => void;
  onDragEnd?: () => void;
  onContextMenu?: (e: React.MouseEvent, keyword: KeywordWithIntent, productId: string) => void;
}

const KeywordPill: React.FC<KeywordPillProps> = ({
  keyword,
  productId,
  isSelected,
  isCustom,
  showVolume,
  showIntentScore,
  intentColor,
  intentLabel,
  onToggle,
  onDelete,
  onRemoveCopy,
  onDragStart,
  onDragEnd,
  onContextMenu,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{ top?: number; bottom?: number; left: number; position: 'top' | 'bottom' }>({ left: 0, position: 'top' });
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const badgeRef = React.useRef<HTMLDivElement | null>(null);
  const isCopied = isCopiedKeyword(keyword);

  const handleDragStart = (e: React.DragEvent) => {
    if (onDragStart) {
      onDragStart(e, keyword, productId);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onContextMenu) {
      onContextMenu(e, keyword, productId);
    }
  };

  const calculateTooltipPosition = () => {
    if (!badgeRef.current) return;
    
    const rect = badgeRef.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const tooltipHeight = 150; // Approximate height of tooltip
    
    // Calculate horizontal center position
    const left = rect.left + rect.width / 2;
    
    // Determine if tooltip should appear above or below
    if (spaceAbove < tooltipHeight && spaceBelow > tooltipHeight) {
      // Show below
      setTooltipPosition({
        top: rect.bottom + 8,
        left,
        position: 'bottom'
      });
    } else {
      // Show above (default)
      setTooltipPosition({
        bottom: window.innerHeight - rect.top + 8,
        left,
        position: 'top'
      });
    }
  };

  const handleMouseEnterBadge = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    
    calculateTooltipPosition();
    setShowTooltip(true);
  };

  // Recalculate position on scroll or resize
  useEffect(() => {
    if (showTooltip) {
      const handleUpdate = () => calculateTooltipPosition();
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [showTooltip]);

  const handleMouseLeaveBadge = () => {
    // Add a small delay before hiding to allow moving to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      if (!isHoveringTooltip) {
        setShowTooltip(false);
      }
    }, 100);
  };

  const handleMouseEnterTooltip = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsHoveringTooltip(true);
  };

  const handleMouseLeaveTooltip = () => {
    setIsHoveringTooltip(false);
    setShowTooltip(false);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div
      className={`relative inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors border cursor-move ${
        isSelected
          ? 'bg-accent-500 text-white border-accent-500'
          : intentColor || 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200'
      } ${isCustom || isCopied ? 'group' : ''}`}
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onContextMenu={handleContextMenu}
    >
      {/* Paperclip Badge for Copied Keywords */}
      {isCopied && keyword.copiedFrom && (
        <div
          ref={badgeRef}
          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs cursor-help"
          onMouseEnter={handleMouseEnterBadge}
          onMouseLeave={handleMouseLeaveBadge}
        >
          📎
        </div>
      )}

      {/* Tooltip for Copy Origin - Rendered as Portal */}
      {showTooltip && isCopied && keyword.copiedFrom && ReactDOM.createPortal(
        <div 
          style={{
            position: 'fixed',
            ...(tooltipPosition.top !== undefined ? { top: tooltipPosition.top } : { bottom: tooltipPosition.bottom }),
            left: tooltipPosition.left,
            transform: 'translateX(-50%)',
            zIndex: 9999
          }}
          onMouseEnter={handleMouseEnterTooltip}
          onMouseLeave={handleMouseLeaveTooltip}
        >
          <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-2xl whitespace-nowrap">
            <div className="font-semibold mb-1">Copied from:</div>
            <div>{keyword.copiedFrom.sourceName}</div>
            <div className="text-gray-300 text-xs mt-1">
              ({keyword.copiedFrom.viewType})
            </div>
            <div className="text-gray-400 text-xs mt-1">
              on {formatDate(keyword.copiedFrom.copiedAt)}
            </div>
            {onRemoveCopy && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTooltip(false);
                  setIsHoveringTooltip(false);
                  onRemoveCopy(productId, keyword.text);
                }}
                className="mt-2 w-full bg-red-600 hover:bg-red-700 text-white text-xs py-1 px-2 rounded transition-colors"
              >
                Remove copy
              </button>
            )}
          </div>
          {/* Tooltip arrow */}
          <div className={`absolute left-1/2 transform -translate-x-1/2 ${
            tooltipPosition.position === 'top' ? 'top-full -mt-1' : 'bottom-full -mb-1'
          }`}>
            <div className={`border-4 border-transparent ${
              tooltipPosition.position === 'top' ? 'border-t-gray-900' : 'border-b-gray-900'
            }`}></div>
          </div>
        </div>,
        document.body
      )}

      {/* Main Keyword Button */}
      <button
        onClick={() => onToggle(productId, keyword.text)}
        className="flex flex-col items-center"
        title={`${intentLabel ? `${intentLabel}: ` : ''}${keyword.text}${keyword.keyword_eng ? ` (${keyword.keyword_eng})` : ''}${showVolume && keyword.volume ? ` (Volume: ${keyword.volume.toLocaleString()})` : ''}${showIntentScore && keyword.intent_score !== undefined ? ` (Intent: ${keyword.intent_score.toFixed(2)})` : ''}`}
      >
        <span>{keyword.text}</span>
        {keyword.keyword_eng && (
          <span className={`text-xs italic font-normal ${isSelected ? 'text-white opacity-90' : 'text-gray-600'}`}>
            {keyword.keyword_eng}
          </span>
        )}
        {(showVolume || showIntentScore) && (
          <div className="flex gap-1.5 text-xs opacity-75 font-normal">
            {showVolume && (
              <span className={isSelected ? 'text-white' : ''}>
                Vol: {keyword.volume ? keyword.volume.toLocaleString() : 'N/A'}
              </span>
            )}
            {showIntentScore && keyword.intent_score !== undefined && (
              <span className={isSelected ? 'text-white' : ''}>
                Int: {keyword.intent_score.toFixed(2)}
              </span>
            )}
          </div>
        )}
      </button>

      {/* Delete Button for Custom Keywords */}
      {isCustom && onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(productId, keyword.text);
          }}
          className={`ml-1 opacity-0 group-hover:opacity-100 transition-opacity ${
            isSelected ? 'text-white hover:text-red-200' : 'text-gray-600 hover:text-red-600'
          }`}
          title="Delete custom keyword"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default KeywordPill;
